<template>
  <div>
    <!-- Trigger button -->
    <button
      class="fixed bottom-20 left-4 z-40 flex items-center justify-center w-9 h-9 rounded-full bg-white border border-gray-200 shadow-md text-gray-500 hover:text-orange-500 hover:border-orange-300 transition-colors"
      title="Manage announcements for this page"
      @click="open"
    >
      <FontAwesomeIcon :icon="['fas', 'bullhorn']" class="text-sm" />
      <span
        v-if="hasActiveModal"
        class="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full border border-white"
      />
    </button>

    <!-- Backdrop -->
    <div
      v-if="isOpen"
      class="fixed inset-0 z-40 bg-black/20"
      @click="close"
    />

    <!-- Slide-out panel -->
    <div
      class="fixed top-0 left-0 h-full w-96 bg-white shadow-xl z-50 flex flex-col transition-transform duration-200"
      :class="isOpen ? 'translate-x-0' : '-translate-x-full'"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
        <div>
          <h2 class="text-sm font-semibold text-gray-900">Announcements</h2>
          <p class="text-xs text-gray-500 font-mono mt-0.5" v-html="wrappablePath" />
        </div>
        <button class="text-gray-400 hover:text-gray-600 text-lg leading-none" @click="close">&times;</button>
      </div>

      <!-- Scrollable body -->
      <div class="flex-1 overflow-y-auto p-4 space-y-6">

        <!-- Section 1: Active modals -->
        <div>
          <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Active on this route</h3>

          <div v-if="loadingActive" class="text-xs text-gray-400 py-2">Loading...</div>

          <!-- Fetch failed -->
          <div
            v-else-if="fetchError"
            class="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-4 text-center"
          >{{ fetchError }}</div>

          <!-- Has one or more active modals -->
          <div v-else-if="activeModals.length > 0" class="space-y-3">
            <div
              v-for="modal in activeModals"
              :key="modal.id"
              class="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2.5"
            >
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">Title</label>
                <input
                  v-model="editedModals[modal.id].title"
                  type="text"
                  class="w-full text-xs border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">Body</label>
                <textarea
                  v-model="editedModals[modal.id].body"
                  rows="6"
                  class="w-full text-xs border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300 resize-y"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">CTA Text</label>
                <input
                  v-model="editedModals[modal.id].cta_text"
                  type="text"
                  class="w-full text-xs border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
                />
              </div>

              <div>
                <div class="flex items-center justify-between mb-1">
                  <label class="block text-xs font-medium text-gray-600">Visible to</label>
                  <button
                    v-if="currentTenantMatch"
                    type="button"
                    class="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                    @click="onlyCurrentTenantFor(modal.id)"
                  >Only {{ currentTenantMatch.name }}</button>
                </div>
                <p class="text-[10px] text-gray-400 mb-1.5">Leave empty to show to every tenant. Select one or more to restrict visibility.</p>
                <div class="flex items-center gap-1.5 flex-wrap mb-1.5">
                  <span
                    v-if="editedModals[modal.id].tenants.length === 0"
                    class="inline-flex items-center text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200 rounded-full px-2 py-0.5"
                  >All tenants</span>
                  <span
                    v-for="tenantId in editedModals[modal.id].tenants"
                    :key="tenantId"
                    class="inline-flex items-center gap-1 text-[10px] font-medium bg-orange-100 text-orange-700 border border-orange-300 rounded-full pl-2 pr-1 py-0.5"
                  >
                    {{ tenantName(tenantId) }}
                    <button
                      type="button"
                      class="rounded-full hover:bg-orange-200 p-0.5 leading-none"
                      title="Remove"
                      @click="removeTenant(modal.id, tenantId)"
                    ><FontAwesomeIcon :icon="['fas', 'xmark']" class="text-[8px]" /></button>
                  </span>
                </div>
                <SimpleMultiSelect
                  v-model="editedModals[modal.id].tenants"
                  :options="tenantOptions"
                  placeholder="Add tenant..."
                  searchable
                />
              </div>

              <div class="pt-1 flex items-center gap-2 text-[10px] text-gray-400">
                <span class="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{{ modal.key }}</span>
                <span>·</span>
                <span>Created {{ formatDate(modal.created_at) }}</span>
                <span v-if="isDirty(modal)" class="ml-auto inline-flex items-center gap-1 text-amber-600 font-medium">
                  <span class="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Unsaved
                </span>
              </div>

              <div v-if="cardState[modal.id].error" class="text-xs text-red-600">{{ cardState[modal.id].error }}</div>
              <div v-if="cardState[modal.id].success" class="text-xs text-green-600">Saved.</div>

              <div class="flex gap-2">
                <button
                  :disabled="cardState[modal.id].saving || !isDirty(modal)"
                  class="flex-1 px-3 py-1.5 text-xs font-medium bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50 transition-colors"
                  @click="saveEdits(modal)"
                >{{ cardState[modal.id].saving ? 'Saving...' : 'Save changes' }}</button>
                <button
                  :disabled="cardState[modal.id].saving"
                  class="px-3 py-1.5 text-xs font-medium text-red-600 border border-red-200 rounded hover:bg-red-50 disabled:opacity-50 transition-colors"
                  @click="confirmDeactivate(modal)"
                >Deactivate</button>
              </div>
            </div>
          </div>

          <!-- No active modals -->
          <div v-else class="text-xs text-gray-400 bg-gray-50 border border-gray-200 rounded-lg px-3 py-4 text-center">
            No active announcements for this route.
          </div>
        </div>

        <!-- Divider -->
        <div class="border-t border-gray-200" />

        <!-- Section 2: Create new -->
        <div>
          <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Create new</h3>

          <div class="space-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Route prefix</label>
              <input
                v-model="createForm.route_path_prefix"
                type="text"
                placeholder="/insight"
                class="w-full text-xs font-mono border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
              <p class="text-[10px] text-gray-400 mt-1">Modal appears on any path that starts with this prefix.</p>
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Title</label>
              <input
                v-model="createForm.title"
                type="text"
                class="w-full text-xs border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">Body</label>
              <textarea
                v-model="createForm.body"
                rows="6"
                class="w-full text-xs border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-y"
              />
            </div>

            <div>
              <label class="block text-xs font-medium text-gray-600 mb-1">CTA Text</label>
              <input
                v-model="createForm.cta_text"
                type="text"
                placeholder="Got it"
                class="w-full text-xs border border-gray-200 rounded px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>

            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-medium text-gray-600">Visible to</label>
                <button
                  v-if="currentTenantMatch"
                  type="button"
                  class="text-[10px] text-blue-600 hover:text-blue-800 font-medium"
                  @click="onlyCurrentTenant"
                >Only {{ currentTenantMatch.name }}</button>
              </div>
              <p class="text-[10px] text-gray-400 mb-1.5">Leave empty to show to every tenant. Select one or more to restrict visibility.</p>
              <SimpleMultiSelect
                v-model="createForm.tenants"
                :options="tenantOptions"
                placeholder="All tenants"
                searchable
              />
            </div>

            <div v-if="createError" class="text-xs text-red-600">{{ createError }}</div>
            <div v-if="createNotice" class="text-xs text-gray-500">{{ createNotice }}</div>

            <button
              :disabled="creating || !createForm.title || !createForm.body || !createForm.route_path_prefix"
              class="w-full px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
              @click="create"
            >{{ creating ? 'Creating...' : 'Create announcement' }}</button>
          </div>
        </div>

      </div>
    </div>

    <ConfirmationModal
      :is-visible="!!deactivateTarget"
      :message="deactivateMessage"
      @confirm="runDeactivate"
      @cancel="cancelDeactivate"
    />
  </div>
</template>

<script>
import axios from 'axios';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import abortable from '@/mixins/abortable';

export default {
  name: 'AnnouncementEditor',

  components: {
    SimpleMultiSelect,
    ConfirmationModal,
  },

  mixins: [abortable],

  data() {
    return {
      isOpen: false,
      loadingActive: false,
      fetchError: null,
      activeModals: [],
      allTenants: [],
      editedModals: {},
      originalModals: {},
      cardState: {},
      deactivateTarget: null,
      creating: false,
      createError: null,
      createNotice: null,
      createForm: {
        route_path_prefix: '',
        title: '',
        body: '',
        cta_text: 'Got it',
        tenants: [],
      },
    };
  },

  computed: {
    currentPath() {
      return this.$route.path;
    },
    wrappablePath() {
      return this.$route.path.replace(/\//g, '/<wbr>');
    },
    hasActiveModal() {
      return this.activeModals.length > 0;
    },
    tenantOptions() {
      return this.allTenants.map(tenant => ({ value: tenant.id, label: tenant.name }));
    },
    currentTenantMatch() {
      const slug = this.$store.getters['tenant/current_tenant_slug'];
      return this.allTenants.find(tenant => tenant.slug === slug) || null;
    },
    deactivateMessage() {
      if (!this.deactivateTarget) return '';
      return `Deactivate "${this.deactivateTarget.key}"? It will immediately stop showing to every tenant, and this panel has no way to bring it back — you'd need to create a new announcement.`;
    },
  },

  watch: {
    '$route'() {
      if (this.isOpen) {
        this.fetchActiveModal();
      }
      this.createForm.route_path_prefix = this.$route.path;
    },
  },

  methods: {
    open() {
      this.isOpen = true;
      this.createForm.route_path_prefix = this.$route.path;
      this.fetchActiveModal();
    },

    close() {
      this.isOpen = false;
      this.createError = null;
      this.createNotice = null;
    },

    registerModal(modal) {
      const snapshot = {
        title: modal.title,
        body: modal.body,
        cta_text: modal.cta_text,
        tenants: (modal.tenants || []).slice(),
      };
      this.editedModals[modal.id] = { ...snapshot, tenants: snapshot.tenants.slice() };
      this.originalModals[modal.id] = snapshot;
      this.cardState[modal.id] = { saving: false, error: null, success: false };
    },

    tenantName(id) {
      const tenant = this.allTenants.find(t => t.id === id);
      return tenant ? tenant.name : `Tenant ${id}`;
    },

    removeTenant(modalId, tenantId) {
      this.editedModals[modalId].tenants = this.editedModals[modalId].tenants.filter(id => id !== tenantId);
    },

    onlyCurrentTenantFor(modalId) {
      if (this.currentTenantMatch) {
        this.editedModals[modalId].tenants = [this.currentTenantMatch.id];
      }
    },

    tenantsEqual(a, b) {
      if (a.length !== b.length) return false;
      const setB = new Set(b);
      return a.every(id => setB.has(id));
    },

    isDirty(modal) {
      const edited = this.editedModals[modal.id];
      const original = this.originalModals[modal.id];
      if (!edited || !original) return false;
      return edited.title !== original.title
        || edited.body !== original.body
        || edited.cta_text !== original.cta_text
        || !this.tenantsEqual(edited.tenants, original.tenants);
    },

    async fetchActiveModal() {
      const signal = this._renewAbortSignal();
      this.loadingActive = true;
      this.fetchError = null;
      this.deactivateTarget = null;
      try {
        const response = await axios.get('/api/announcements/manage/', {
          params: { path: this.$route.path },
          signal,
        });
        this.activeModals = response.data.modals || [];
        this.allTenants = response.data.tenants || [];
        this.editedModals = {};
        this.originalModals = {};
        this.cardState = {};
        this.activeModals.forEach(modal => this.registerModal(modal));
      } catch (error) {
        if (this._isAborted(error)) return;
        this.activeModals = [];
        this.editedModals = {};
        this.originalModals = {};
        this.cardState = {};
        this.fetchError = 'Could not load announcements for this route.';
      } finally {
        if (!signal.aborted) this.loadingActive = false;
      }
    },

    async saveEdits(modal) {
      const edited = this.editedModals[modal.id];
      const state = this.cardState[modal.id];
      state.saving = true;
      state.error = null;
      state.success = false;
      try {
        const response = await axios.patch(`/api/announcements/manage/${modal.id}/`, {
          title: edited.title,
          body: edited.body,
          cta_text: edited.cta_text,
          tenants: edited.tenants,
        });
        const updated = response.data.modal || response.data;
        const idx = this.activeModals.findIndex(m => m.id === modal.id);
        if (idx !== -1) this.activeModals.splice(idx, 1, updated);
        const snapshot = {
          title: updated.title,
          body: updated.body,
          cta_text: updated.cta_text,
          tenants: (updated.tenants || []).slice(),
        };
        this.editedModals[modal.id] = { ...snapshot, tenants: snapshot.tenants.slice() };
        this.originalModals[modal.id] = snapshot;
        state.success = true;
        setTimeout(() => { state.success = false; }, 3000);
      } catch {
        state.error = 'Failed to save. Please try again.';
      } finally {
        state.saving = false;
      }
    },

    confirmDeactivate(modal) {
      this.deactivateTarget = modal;
    },

    cancelDeactivate() {
      this.deactivateTarget = null;
    },

    async runDeactivate() {
      const modal = this.deactivateTarget;
      this.deactivateTarget = null;
      const state = this.cardState[modal.id];
      if (!state) return;
      state.saving = true;
      state.error = null;
      try {
        await axios.patch(`/api/announcements/manage/${modal.id}/`, { active: false });
        this.activeModals = this.activeModals.filter(m => m.id !== modal.id);
        delete this.editedModals[modal.id];
        delete this.originalModals[modal.id];
        delete this.cardState[modal.id];
      } catch {
        state.error = 'Failed to deactivate. Please try again.';
        state.saving = false;
      }
    },

    onlyCurrentTenant() {
      if (this.currentTenantMatch) {
        this.createForm.tenants = [this.currentTenantMatch.id];
      }
    },

    async create() {
      this.creating = true;
      this.createError = null;
      this.createNotice = null;
      try {
        const response = await axios.post('/api/announcements/manage/', {
          route_path_prefix: this.createForm.route_path_prefix,
          title: this.createForm.title,
          body: this.createForm.body,
          cta_text: this.createForm.cta_text || 'Got it',
          tenants: this.createForm.tenants,
        });
        const created = response.data.modal || response.data;
        if (this.$route.path.startsWith(created.route_path_prefix)) {
          this.registerModal(created);
          this.activeModals.push(created);
        } else {
          this.createNotice = `Created. It won't appear above — "${created.route_path_prefix}" doesn't match the current route.`;
        }
        this.createForm.title = '';
        this.createForm.body = '';
        this.createForm.cta_text = 'Got it';
        this.createForm.tenants = [];
      } catch (err) {
        this.createError = err.response?.data?.error || 'Failed to create. Please try again.';
      } finally {
        this.creating = false;
      }
    },

    formatDate(isoString) {
      if (!isoString) return '';
      const [y, m, d] = isoString.substring(0, 10).split('-');
      return new Date(y, m - 1, d).toLocaleDateString();
    },
  },
};
</script>
